import type { NrtMemberBlockReason } from './vocabularies';

// ─── NRT refusal vocabulary ──────────────────────────────────────────────────
//
// The closed set of `error.code` values every NRT route may return (ADR-002
// Rule 4: stable, snake_case, asserted on by clients, never the message).
//
// This constant carries the contract's central invariant: every way a request
// can be refused is a named, typed response the caller can act on. Two tests
// hold it to that — one asserting set-equality between the codes the route
// contracts document and the values here, in both directions, and one asserting
// each route documents the full set attributed to it. A code nothing documents
// is dead; a documented code missing from here is a refusal the client cannot
// branch on. Neither can land.
//
// snake_case throughout, matching the repo's dominant `error('bad_request', …)`
// convention and ADR-002 Rule 4. (The care-scheduling surface uses kebab-case;
// it is the outlier, not the precedent.)

/**
 * The member pre-conditions, keyed by accessor name and **derived from
 * `NRT_MEMBER_BLOCK_REASON`** rather than re-typed.
 *
 * The derivation is the point. An earlier draft listed these six literals here
 * and asserted set-equality against the block reasons in a test — but the test
 * compared production against a hand-written copy in the test file, so adding a
 * seventh pre-condition code passed cleanly. Construction beats assertion: a
 * pre-condition that is not a block reason is now unrepresentable, because
 * there is only one place to add one.
 *
 * Each value's meaning:
 * - `no_shipping_address` — no shipping row and no home row to fall back to.
 * - `no_verified_name` — no profile row, or legal first/last name blank.
 * - `request_awaiting_approval` — an existing request has not yet been reviewed
 *   by a care team member. NOT "an order exists": an `approved` order does not
 *   block, because the decision this gate waits on has already been made.
 * - `not_enrolled_in_programme` — no active enrolment to attach the order to.
 * - `unsupported_shipping_country` — the resolved address is outside the
 *   shippable set. Named and returned rather than logged-and-dropped: the
 *   legacy path took a silent early return here, which left the request
 *   looking accepted while nothing would ever ship.
 * - `nrt_not_entitled` — the member's client does not buy NRT fulfilment.
 *   1.0 gated on `isOptedInToNrt`; the field is `nrt_enabled` on company
 *   config. A client gate, not a member one: two members of the same client
 *   always get the same answer.
 * - `previous_orders_pending` — an order imported from the 1.0 app is still
 *   awaiting a care-team decision, whichever of the member's requests is
 *   newest. Separate from `request_awaiting_approval`, whose copy speaks of the
 *   member's last request, because the order waiting may be an older one.
 * - `migration_import_incomplete` — the member migrated from the 1.0 app and
 *   their order history has not finished coming across, so nothing here can
 *   yet say whether an order of theirs is waiting. Its own code, and its own
 *   copy, because nothing is under review: only an operator replay of the
 *   import releases the member, and the copy must not imply an approval will.
 *   Both are returned only by the create route — the supply read never
 *   surfaces them.
 */
export const MEMBER_PRECONDITION_CODES = {
  noShippingAddress: 'no_shipping_address',
  noVerifiedName: 'no_verified_name',
  requestAwaitingApproval: 'request_awaiting_approval',
  notEnrolledInProgramme: 'not_enrolled_in_programme',
  unsupportedShippingCountry: 'unsupported_shipping_country',
  nrtNotEntitled: 'nrt_not_entitled',
  previousOrdersPending: 'previous_orders_pending',
  migrationImportIncomplete: 'migration_import_incomplete',
} as const satisfies Record<string, NrtMemberBlockReason>;

// Both directions of the pairing, enforced by the compiler rather than by a
// test that could be written against a stale copy:
//
//   `satisfies Record<string, NrtMemberBlockReason>` above rejects a
//   pre-condition code that is not a block reason.
//
//   The alias below rejects a block reason that has no pre-condition code —
//   it resolves to `never`, and the annotation then fails to compile, if the
//   values above do not cover the union. Adding a seventh block reason
//   therefore breaks the build here until its code is added, instead of
//   shipping a read-side state the write side cannot produce.
type UncoveredBlockReason = Exclude<
  NrtMemberBlockReason,
  (typeof MEMBER_PRECONDITION_CODES)[keyof typeof MEMBER_PRECONDITION_CODES]
>;
// AND IT HELD IN BOTH DIRECTIONS, which is the case it was not designed for.
// `nrt_not_entitled` was deleted on the belief that "NRT is universal for
// enrolled tobacco members at Gamma" — written 2026-09-15, and wrong by
// 2026-09-16, when it was confirmed that NRT fulfilment is per-client and the
// flag is already on company config. Restoring it meant adding a block reason
// AND its precondition code; deleting it had meant removing both. Taking
// either one alone fails the build: drop the code and this alias resolves to
// `never`; drop the reason and the `satisfies` above rejects the orphaned code.
// A test asserting the same pairing would have had to be remembered and kept in
// step; a type cannot be forgotten, and it fires in both directions of change.
type EveryBlockReasonHasACode = [UncoveredBlockReason] extends [never] ? true : never;
const _everyBlockReasonHasACode: EveryBlockReasonHasACode = true;
void _everyBlockReasonHasACode;

export const NRT_ERROR_CODES = {
  // ── member pre-conditions ────────────────────────────────────────────────
  // Spread, not re-typed: every one of these is by construction also an
  // NRT_MEMBER_BLOCK_REASON surfaced on the supply read, so the app never
  // offers an action whose write is guaranteed to refuse.
  ...MEMBER_PRECONDITION_CODES,

  // ── member write-time conflicts ──────────────────────────────────────────
  /**
   * `expectedAddressId` no longer matches the resolved current address — the
   * member moved between reading the form and submitting it. Refused so the
   * parcel cannot go somewhere they did not confirm.
   */
  addressChanged: 'address_changed',
  /**
   * An `idempotencyKey` already seen for this member was replayed carrying a
   * DIFFERENT body.
   *
   * Without this code the key is a hole rather than a safety net: the handler's
   * only options would be to replay the first result — silently discarding a
   * second, genuinely different request behind a success response — or to
   * refuse with a code no client can branch on, because the closure tests
   * assert the documented set and the published vocabulary equal in both
   * directions. The first is the accepted-but-nothing-ships shape this whole
   * contract exists to forbid, reintroduced through the field meant to make
   * retries safe.
   *
   * The key therefore binds to a request fingerprint: replay on an identical
   * body, refuse here on a mismatch.
   */
  idempotencyKeyReuse: 'idempotency_key_reuse',
  /** Rolling-window rate limit; `details.retryAfterSeconds` carries the wait. */
  rateLimited: 'rate_limited',

  // ── staff order-state conflicts ──────────────────────────────────────────
  /** Unknown request id on the staff per-request read. */
  requestNotFound: 'request_not_found',
  /** Unknown order id. */
  orderNotFound: 'order_not_found',
  /**
   * A staff order names a member the caller may not act for — outside their
   * caseload — or a member who does not exist. ONE code for both, on purpose: a
   * caller who may not see the member must not be able to tell which.
   */
  memberNotFound: 'member_not_found',
  /**
   * A staff order for a member who already has an order awaiting review in the
   * same program. Scoped to the program, not global: an order pending under
   * another program does not block this one.
   */
  orderPendingReview: 'order_pending_review',
  /** Items cannot be changed once the order has left `created`. */
  orderNotEditable: 'order_not_editable',
  /** Approve attempted from a status other than `created`. */
  orderNotApprovable: 'order_not_approvable',
  /**
   * Approve attempted on an order with no items. Approving an empty order is
   * the staff-side spelling of shipping nothing while telling the member
   * otherwise.
   */
  orderHasNoItems: 'order_has_no_items',
  /**
   * Approve attempted on an order whose request carries member-authored free
   * text, without the caller acknowledging having seen it.
   *
   * H-1's gate, and the honest description of it is ATTESTATION, not proof: a
   * client can send the flag without rendering anything, and no API can verify
   * that a human read a field. What it removes is the SILENT case — approving
   * with no signal at all that detail exists — which is the shape H-1 exists to
   * close, and it is auditable because the claim is in the request while the
   * approving internal_user_id is on the status event the approval writes.
   *
   * The remaining reliance on the UI not rendering approve until the detail
   * panel is open is convention, is not enforced here, and is recorded as a
   * residual rather than described as solved.
   */
  otherDetailUnacknowledged: 'other_detail_unacknowledged',
  /** Reject attempted from a status other than `created`. */
  orderNotRejectable: 'order_not_rejectable',
  /** Ship attempted from a status other than `approved` or `submitted`. */
  orderNotShippable: 'order_not_shippable',
  /**
   * Any staff transition attempted on an order `historicalImportedOrder()`
   * marks as history: a 1.0 import no request row points at.
   *
   * Its own code rather than one of the four status refusals beside it,
   * because those mean "your view is behind, re-read and try again", and a
   * re-read changes nothing here. History is read-only whatever status it
   * sits at — many imports sit at `created` forever.
   */
  orderHistorical: 'order_historical',

  // ── validation ───────────────────────────────────────────────────────────
  /** Body failed schema validation. */
  invalidRequest: 'invalid_request',
  /** A side-effect value outside the closed wire vocabulary. */
  invalidSideEffect: 'invalid_side_effect',
  /** A SKU the catalogue does not recognise. */
  unknownSku: 'unknown_sku',
  /** The same SKU appeared twice in one item set. */
  duplicateSku: 'duplicate_sku',
  /** A non-integer quantity, or one below 1. */
  invalidQuantity: 'invalid_quantity',

  // ── authorization / infrastructure ───────────────────────────────────────
  /**
   * No usable bearer token.
   *
   * The value is `invalid_token` because that is what the shared auth gate
   * actually emits (`apps/api/src/identity/authorize-staff.ts`, and 70-odd
   * other sites). An earlier revision of this file invented `unauthenticated`
   * — a code no route in the repo returns — so a client branching on it would
   * have found the branch never taken while every real 401 fell through to a
   * generic failure. A published vocabulary has to name what the runtime says,
   * not what reads well.
   */
  invalidToken: 'invalid_token',
  /** Authenticated, but not authorized for this surface. */
  forbidden: 'forbidden',
  /**
   * Authenticated staff with no active internal-provisioning row — a distinct
   * 403 the shared gate emits (`deny403`'s `'forbidden' | 'not_invited'`) and
   * the one a client turns into a "request access" affordance rather than a
   * generic denial. Omitting it is why it is here: the first revision listed
   * only `forbidden`, so that affordance could never render.
   */
  notInvited: 'not_invited',
  /** Unhandled server-side failure (ADR-002 Rule 6). */
  internalError: 'internal_error',
} as const;

export type NrtErrorCode = (typeof NRT_ERROR_CODES)[keyof typeof NRT_ERROR_CODES];

/**
 * Every code as a flat, readonly array — the set the contract tests compare
 * against. Derived from the object rather than written out again, so a code
 * cannot exist in one and not the other.
 *
 * There is deliberately no `z.enum` over this: nothing parses an error code
 * off the wire. ADR-002's envelope types `error.code` as a plain string and
 * clients branch on the constants above, so an enum schema would buy nothing
 * and would need a tuple cast that ADR-117 rules out.
 */
export const NRT_ERROR_CODE_VALUES: readonly NrtErrorCode[] = Object.freeze(
  Object.values(NRT_ERROR_CODES),
);
